import React, { useState, useEffect, useCallback } from 'react';
import texasApi from '../../services/texasApi';

export interface DeadlineItem {
  pk?: string;
  sk?: string;
  family: string | null;
  expiresAtEpoch: number | null;
  expiresAtISO: string | null;
  nativeTtlEpoch: number | null;
  expired: boolean;
  purgeWithinHours: number;
}

export interface FamilySummary {
  active: number;
  expired: number;
  nextDeadlineISO: string | null;
}

export interface DeadlineSummary {
  byFamily: Record<string, FamilySummary>;
  totalActive: number;
  totalExpired: number;
  generatedAt: string;
}

export interface DeadlinePolicy {
  graceHours: number;
  tableTtlAttribute: string;
  families: Record<string, { archiveAfterDays?: number; [k: string]: unknown }>;
  note: string;
}

function unwrap<T>(res: any): T | null {
  const body = res?.data;
  if (!body || typeof body !== 'object') return null;
  if ('success' in body) return body.success ? ((body.data ?? null) as T) : null;
  return ((body.data ?? body) as T) ?? null;
}

function apiError(res: any): string | null {
  const body = res?.data;
  if (body && typeof body === 'object' && body.success === false) {
    return body.message ?? body.error ?? 'Request failed';
  }
  return null;
}

export const TexasDeadlineMonitor: React.FC = () => {
  const [deadlines, setDeadlines] = useState<DeadlineItem[]>([]);
  const [activeFilters, setActiveFilters] = useState<{ family: string | null; includeExpired: boolean }>({ family: null, includeExpired: false });
  const [activeGeneratedAt, setActiveGeneratedAt] = useState<string | null>(null);
  const [activeError, setActiveError] = useState<string | null>(null);

  const [summary, setSummary] = useState<DeadlineSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [policy, setPolicy] = useState<DeadlinePolicy | null>(null);
  const [policyError, setPolicyError] = useState<string | null>(null);

  const [family, setFamily] = useState('');
  const [includeExpired, setIncludeExpired] = useState(false);

  const loadActive = useCallback(async (opts: { family: string; includeExpired: boolean }) => {
    setActiveError(null);
    try {
      const res = await texasApi.deadlineMonitor.listActive({
        family: opts.family || undefined,
        includeExpired: opts.includeExpired || undefined,
      });
      const err = apiError(res);
      if (err) { setActiveError(err); setDeadlines([]); setActiveGeneratedAt(null); return; }
      const data = unwrap<{ deadlines?: DeadlineItem[]; generatedAt?: string }>(res);
      setDeadlines(data?.deadlines ?? []);
      setActiveGeneratedAt(data?.generatedAt ?? null);
      setActiveFilters({ family: opts.family || null, includeExpired: opts.includeExpired });
    } catch (e: any) {
      setActiveError(e?.message ?? 'Failed to load active deadlines');
      setDeadlines([]);
      setActiveGeneratedAt(null);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    setSummaryError(null);
    try {
      const res = await texasApi.deadlineMonitor.getSummary();
      const err = apiError(res);
      if (err) { setSummaryError(err); setSummary(null); return; }
      setSummary(unwrap<DeadlineSummary>(res));
    } catch (e: any) {
      setSummaryError(e?.message ?? 'Failed to load deadline summary');
      setSummary(null);
    }
  }, []);

  const loadPolicy = useCallback(async () => {
    setPolicyError(null);
    try {
      const res = await texasApi.deadlineMonitor.getPolicy();
      const err = apiError(res);
      if (err) { setPolicyError(err); setPolicy(null); return; }
      setPolicy(unwrap<DeadlinePolicy>(res));
    } catch (e: any) {
      setPolicyError(e?.message ?? 'Failed to load TTL policy');
      setPolicy(null);
    }
  }, []);

  useEffect(() => { loadActive({ family, includeExpired }); }, [loadActive, family, includeExpired]);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadPolicy(); }, [loadPolicy]);

  // Families come from the backend's own summary, never a hardcoded list.
  const families = summary ? Object.keys(summary.byFamily).sort() : [];
  const superAdminOnly = activeError === 'SUPER_ADMIN_REQUIRED';
  const unknownFamily = activeError === 'UNKNOWN_FAMILY';

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="deadline-monitor">
      <h1 className="text-2xl font-bold text-gray-900">
        <i className="fas fa-clock mr-2" />Deadline Monitor
      </h1>

      <section className="bg-white shadow rounded-lg p-6 space-y-4" data-testid="family-summary">
        <h2 className="font-bold text-indigo-600">Expiry Timeline by Family (F-135)</h2>

        {summaryError && (
          <div role="alert" data-testid="summary-error" className="p-4 bg-red-50 rounded border border-red-300 text-red-800">
            {summaryError}
          </div>
        )}

        {summary && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-gray-50 rounded border">
                <div className="text-sm text-gray-600">Active deadlines</div>
                <div className="text-2xl font-bold text-gray-900" data-testid="total-active">{summary.totalActive}</div>
              </div>
              <div className="p-4 bg-gray-50 rounded border">
                <div className="text-sm text-gray-600">Expired (pre-purge)</div>
                <div className="text-2xl font-bold text-gray-900" data-testid="total-expired">{summary.totalExpired}</div>
              </div>
              <div className="p-4 bg-gray-50 rounded border">
                <div className="text-sm text-gray-600">Generated at</div>
                <div className="text-sm font-semibold text-gray-900" data-testid="summary-generated-at">{summary.generatedAt}</div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-sm font-semibold">Family</th>
                    <th className="px-4 py-3 text-sm font-semibold">Active</th>
                    <th className="px-4 py-3 text-sm font-semibold">Expired</th>
                    <th className="px-4 py-3 text-sm font-semibold">Next deadline</th>
                    <th className="px-4 py-3 text-sm font-semibold">Archive stream</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Object.keys(summary.byFamily).sort().map((fam) => {
                    const row = summary.byFamily[fam];
                    const archive = policy?.families?.[fam];
                    return (
                      <tr key={fam} data-testid={`family-row-${fam}`}>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">{fam}</td>
                        <td className="px-4 py-3 text-sm">{row.active}</td>
                        <td className="px-4 py-3 text-sm">{row.expired}</td>
                        <td className="px-4 py-3 text-sm" data-testid={`family-next-${fam}`}>
                          {row.nextDeadlineISO ?? 'none active'}
                        </td>
                        <td className="px-4 py-3 text-sm" data-testid={`family-archive-${fam}`}>
                          {archive
                            ? (archive.archiveAfterDays !== undefined
                              ? `retained ${archive.archiveAfterDays} days`
                              : 'retained')
                            : (policy ? 'no archive policy' : 'policy unavailable')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {policyError && (
        <div role="alert" data-testid="policy-error" className="p-4 bg-red-50 rounded border border-red-300 text-red-800">
          {policyError}
        </div>
      )}

      {policy && (
        <section className="bg-white shadow rounded-lg p-6 space-y-2" data-testid="ttl-policy">
          <h2 className="font-bold text-indigo-600">TTL / Archive Policy</h2>
          <div className="text-sm text-gray-900" data-testid="policy-grace">
            Grace hours: {policy.graceHours} &middot; table TTL attribute: <code>{policy.tableTtlAttribute}</code>
          </div>
          <p className="text-sm text-gray-600" data-testid="policy-note">{policy.note}</p>
        </section>
      )}

      <section className="bg-white shadow rounded-lg p-6 space-y-4" data-testid="active-deadlines">
        <h2 className="font-bold text-indigo-600">Active Deadline Items</h2>

        <div className="flex items-end space-x-2">
          <div>
            <label htmlFor="family-filter" className="block text-sm font-semibold text-gray-900">Family</label>
            <select id="family-filter" data-testid="family-filter" className="p-2 bg-white border rounded"
              value={family} onChange={(e) => setFamily(e.target.value)}>
              <option value="">All families</option>
              {families.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="include-expired" className="block text-sm font-semibold text-gray-900">
              <input id="include-expired" data-testid="include-expired" type="checkbox"
                checked={includeExpired} onChange={(e) => setIncludeExpired(e.target.checked)} />
              {' '}Include expired (super-admin)
            </label>
          </div>
        </div>

        {superAdminOnly && (
          <div role="alert" data-testid="super-admin-required" className="p-4 bg-amber-50 rounded border border-amber-300 text-amber-900">
            The expanded view (includeExpired) is restricted to super-admins. The backend returned
            SUPER_ADMIN_REQUIRED; this is expected for a non-privileged operator, not a fault.
          </div>
        )}

        {unknownFamily && (
          <div role="alert" data-testid="unknown-family" className="p-4 bg-red-50 rounded border border-red-300 text-red-800">
            The backend rejected this family filter as UNKNOWN_FAMILY. Pick a family from the list above.
          </div>
        )}

        {activeError && !superAdminOnly && !unknownFamily && (
          <div role="alert" data-testid="active-error" className="p-4 bg-red-50 rounded border border-red-300 text-red-800">
            {activeError}
          </div>
        )}

        {activeGeneratedAt && (
          <p className="text-sm text-gray-600" data-testid="active-generated-at">
            View generated at {activeGeneratedAt}
            {activeFilters.family && <> &middot; family {activeFilters.family}</>}
            {activeFilters.includeExpired && <> &middot; including expired</>}
          </p>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-sm font-semibold">Family</th>
                <th className="px-4 py-3 text-sm font-semibold">Expires at</th>
                <th className="px-4 py-3 text-sm font-semibold">Expires at epoch (mirror)</th>
                <th className="px-4 py-3 text-sm font-semibold">State</th>
                <th className="px-4 py-3 text-sm font-semibold">Native TTL epoch</th>
                <th className="px-4 py-3 text-sm font-semibold">Purge within (h)</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {deadlines.length === 0 && !activeError && (
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-sm text-gray-500" data-testid="deadlines-empty">
                    0 deadline items in this view.
                  </td>
                </tr>
              )}
              {deadlines.map((d, i) => (
                <tr key={`${d.pk ?? 'dl'}-${d.sk ?? i}`} data-testid={`deadline-row-${i}`}>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{d.family ?? '-'}</td>
                  <td className="px-4 py-3 text-sm" data-testid={`deadline-expires-${i}`}>
                    {d.expiresAtISO ?? 'not reported'}
                  </td>
                  <td className="px-4 py-3 text-sm" data-testid={`deadline-epoch-mirror-${i}`}>
                    {d.expiresAtEpoch ?? '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={d.expired
                        ? 'px-2 py-1 rounded bg-red-100 text-red-900 font-semibold'
                        : 'px-2 py-1 rounded bg-green-100 text-green-900 font-semibold'}
                      data-testid={`deadline-state-${i}`}
                    >
                      {d.expired ? 'EXPIRED' : 'ACTIVE'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{d.nativeTtlEpoch ?? '-'}</td>
                  <td className="px-4 py-3 text-sm">{d.purgeWithinHours}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default TexasDeadlineMonitor;